import {
    ALGOLIA_PRODUCTS_INDEX_FACETS,
    ALGOLIA_PRODUCTS_INDEX_NAME,
    ALGOLIA_QUERY_SUGGESTIONS_INDEX_NAME,
    algoliaGetTrendingItemsAndFacets,
    algoliaSearchForHits,
    algoliaSearchSingleIndex
} from "lib/algolia"
import { reportSentryError } from "lib/sentry"
import type { InstantSearchResultsProductTypes } from 'modules/products'

import {
    SEARCH_PAGE_RESULTS_LIMIT,
    SEARCH_QUERY_INSTANT_RESULTS_DISPLAY_COUNT,
    SEARCH_TRENDING_FACETS_MAX_COUNT,
    SEARCH_TRENDING_PRODUCTS_MAX_COUNT,
    SEARCH_TRENDING_THRESHOLD
} from "./config"
import { normalizeInstantSearchResultsToProductTypes, sanitizeAlgoliaProductSearchResults } from "./utils"

// ─────────────────────────────────────────────────────────────────────────────
// Product search built on top of the Algolia integration.
// Search Dropdown & Search Page data requests.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Get search results and query suggestions data for search dropdown, based on the provided query.
 * This approach uses combined Algolia search and recommendation requests to avoid doubled requests billing.
 */
export const getSearchDropdownSearchResultsData = async (
    query: string,
    userToken: string | undefined
) => {
    try {
        const response = await algoliaSearchForHits([
            {
                indexName: ALGOLIA_PRODUCTS_INDEX_NAME,
                query,
                hitsPerPage: SEARCH_PAGE_RESULTS_LIMIT,
                userToken
            },
            {
                indexName: ALGOLIA_QUERY_SUGGESTIONS_INDEX_NAME,
                query,
                hitsPerPage: SEARCH_QUERY_INSTANT_RESULTS_DISPLAY_COUNT,
                userToken
            }
        ])

        if (!response) throw new Error('Algolia search products and query suggestions data are not available.')

        const productsResponse = response.results[0].hits as unknown as InstantSearchResultsProductTypes[]
        const querySuggestionsResponse = response.results[1].hits as unknown as { query: string }[]
        const querySuggestionsData = querySuggestionsResponse.map((hit) => hit.query)

        const normalizedAgloliaProductSearchResults = normalizeInstantSearchResultsToProductTypes(productsResponse) ?? []
        const sanitizedAlgoliaProductSearchResults = sanitizeAlgoliaProductSearchResults(normalizedAgloliaProductSearchResults) ?? []

        return {
            products: sanitizedAlgoliaProductSearchResults,
            querySuggestions: querySuggestionsData,
            meta: {
                queryID: response.results[0].queryID
            }
        }

    } catch (error) {
        reportSentryError(error, 'Failed to get search results and query suggestions data for search dropdown')

        return {
            products: [],
            querySuggestions: [],
            meta: {
                queryID: null
            }
        }
    }
}

/**
 * Get trending products and trending facetes data for search dropdown.
 * This approach uses combined Algolia search and recommendation requests to avoid multipied requests billing.
 */
export const getSearchDropdownTrendingProductsAndFacetsData = async () => {
    try {
        const response = await algoliaGetTrendingItemsAndFacets({
            indexName: ALGOLIA_PRODUCTS_INDEX_NAME,
            threshold: SEARCH_TRENDING_THRESHOLD,
            maxItems: SEARCH_TRENDING_PRODUCTS_MAX_COUNT,
            facets: [
                { facetName: ALGOLIA_PRODUCTS_INDEX_FACETS.category, maxRecommendations: SEARCH_TRENDING_FACETS_MAX_COUNT },
                { facetName: ALGOLIA_PRODUCTS_INDEX_FACETS.name_short, maxRecommendations: SEARCH_TRENDING_FACETS_MAX_COUNT }
            ]
        })

        if (!response) throw new Error('Algolia trending products and facets data are not available.')

        const trendingProductsResponse = response.results[0].hits as unknown as InstantSearchResultsProductTypes[]
        const trendingCategoryFacetsResponse = response.results[1].hits as unknown as { facetValue: string }[]
        const trendingShortNameFacetsResponse = response.results[2].hits as unknown as { facetValue: string }[]
        const trendingCategoryFacetsData = trendingCategoryFacetsResponse.map((hit) => hit.facetValue)
        const trendingShortNameFacetsData = trendingShortNameFacetsResponse.map((hit) => hit.facetValue)

        const normalizedAgloliaTrendingProducts = normalizeInstantSearchResultsToProductTypes(trendingProductsResponse) ?? []
        const sanitizedAlgoliaTrendingProducts = sanitizeAlgoliaProductSearchResults(normalizedAgloliaTrendingProducts) ?? []

        return {
            products: sanitizedAlgoliaTrendingProducts,
            categoryFacets: trendingCategoryFacetsData,
            shortNameFacets: trendingShortNameFacetsData,
            meta: {
                productsQueryID: response.results[0].queryID,
                categoryFacetsQueryID: response.results[1].queryID,
                shortNameFacetsQueryID: response.results[2].queryID
            }
        }
    } catch (error) {
        reportSentryError(error, 'Failed to get trending products and facets data for search dropdown')

        return {
            products: [],
            categoryFacets: [],
            shortNameFacets: [],
            meta: {
                productsQueryID: null,
                categoryFacetsQueryID: null,
                shortNameFacetsQueryID: null
            }
        }
    }
}

/**
 * Get search results IDs for search page.
 */
export const getSearchPageResultsIDs = async (
    query: string,
    userToken: string | undefined
) => {
    try {
        const response = await algoliaSearchSingleIndex<InstantSearchResultsProductTypes>(
            ALGOLIA_PRODUCTS_INDEX_NAME,
            {
                query,
                hitsPerPage: SEARCH_PAGE_RESULTS_LIMIT,
                page: 0,
                attributesToRetrieve: ['id'],
                userToken
            })

        if (!response) throw new Error('Algolia search products data are not available.')

        // Old or deleted products may have undefined id in Algoliaindex.
        return {
            ids: response.hits.filter((hit) => Boolean(hit.id)).map((hit) => hit.id) ?? [],
            meta: {
                queryID: response.queryID
            }
        }
    } catch (error) {
        reportSentryError(error, 'Failed to get Algolia search page results IDs')

        return {
            ids: [],
            meta: {
                queryID: null
            }
        }
    }
}
